import { OrcamentoIcon as ValidityIcon } from '../components/OrcamentoUI';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   PAINEL DE VALIDADES (SGI — ISO 9001/45001)
   Agrega validades a expirar: trabalhadores (aptidão médica,
   documentos, vistos), equipamentos (revisões, calibrações, DL50),
   instalações (registos de segurança), veículos e empresa
   (certidões/seguros). Dados do endpoint /sistema-integrado/validades.
   ============================================================ */

const CAT_ICON = {
    'Trabalhadores': '👷',
    'Equipamentos': '🛠️',
    'Instalações': '🏢',
    'Veículos': '🚐',
    'Empresa': '🏛️',
    'RMM': '📏',
};

const estadoInfo = (dias) => {
    if (dias < 0) return { label: `Expirado há ${-dias} d`, bg: '#fee2e2', fg: '#991b1b' };
    if (dias === 0) return { label: 'Expira hoje', bg: '#fee2e2', fg: '#991b1b' };
    if (dias <= 7) return { label: `Expira em ${dias} d`, bg: '#ffedd5', fg: '#9a3412' };
    if (dias <= 30) return { label: `Expira em ${dias} d`, bg: '#fef9c3', fg: '#854d0e' };
    return { label: `Expira em ${dias} d`, bg: '#e0f2fe', fg: '#075985' };
};

const fmtData = (d) => (d ? new Date(d).toLocaleDateString('pt-PT') : '-');

const lbl = { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 };
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', background: '#fff' };
const btnSec = { border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', color: '#334155' };
const btnPri = { border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };

const PainelValidades = ({ liquid = false }) => {
    const [horizonte, setHorizonte] = useState(90);
    const [itens, setItens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [catFiltro, setCatFiltro] = useState('Todos');
    const [pesquisa, setPesquisa] = useState('');
    // Lembretes por email
    const [lembrete, setLembrete] = useState(null);
    const [mostrarLembrete, setMostrarLembrete] = useState(false);
    const [msgLembrete, setMsgLembrete] = useState('');
    const [enviando, setEnviando] = useState(false);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/sistema-integrado/validades?horizonte=${horizonte}`);
            setItens(r.data.itens || []);
        } catch (e) {
            console.error('Erro ao carregar validades:', e);
            setItens([]);
        } finally {
            setLoading(false);
        }
    }, [horizonte]);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => { api.get('/sistema-integrado/validades/lembrete').then(r => setLembrete(r.data)).catch(() => {}); }, []);

    const updLembrete = (patch) => setLembrete(prev => ({ ...prev, ...patch }));
    const guardarLembrete = async () => {
        try {
            const r = await api.put('/sistema-integrado/validades/lembrete', {
                ativo: lembrete.lembrete_validades_ativo === 'true',
                dias: parseInt(lembrete.lembrete_validades_dias, 10),
                diasemana: parseInt(lembrete.lembrete_validades_diasemana, 10),
                email: lembrete.lembrete_validades_email,
            });
            setLembrete(r.data);
            setMsgLembrete('✓ Guardado.');
            setTimeout(() => setMsgLembrete(''), 2500);
        } catch { setMsgLembrete('Erro ao guardar.'); }
    };
    const enviarAgora = async () => {
        setEnviando(true); setMsgLembrete('');
        try {
            const r = await api.post('/sistema-integrado/validades/lembrete/enviar', {});
            setMsgLembrete(r.data.itens === 0 ? 'Sem validades a expirar — nada a enviar.' : `✓ Enviado para ${r.data.destino} (${r.data.itens} itens).`);
        } catch (e) { setMsgLembrete(e.response?.data?.error || 'Erro ao enviar.'); }
        finally { setEnviando(false); }
    };

    const categorias = ['Todos', ...Array.from(new Set(itens.map(i => i.categoria)))];

    let visiveis = catFiltro === 'Todos' ? itens : itens.filter(i => i.categoria === catFiltro);
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        visiveis = visiveis.filter(i =>
            (i.entidade || '').toLowerCase().includes(t) ||
            (i.tipo || '').toLowerCase().includes(t) ||
            (i.detalhe || '').toLowerCase().includes(t)
        );
    }

    const nExpirados = itens.filter(i => i.dias < 0).length;
    const n30 = itens.filter(i => i.dias >= 0 && i.dias <= 30).length;

    const DIAS_SEMANA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

    return (
        <div>
            {/* Lembretes por email */}
            <div className="validades-reminder" style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 16px', marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontSize: '0.88rem', color: '#3730a3' }}>
                        {liquid ? <ValidityIcon name="mail" fallback="📧" /> : '📧'} <strong>Lembretes por email</strong>
                        {lembrete && (lembrete.lembrete_validades_ativo === 'true'
                            ? <> — ativos, todas as <strong>{DIAS_SEMANA[parseInt(lembrete.lembrete_validades_diasemana, 10)] || 'segundas'}s</strong>, horizonte {lembrete.lembrete_validades_dias} dias</>
                            : <> — <span style={{ color: '#991b1b' }}>desativados</span></>)}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={enviarAgora} disabled={enviando} style={{ ...btnSec, fontSize: '0.78rem', padding: '5px 12px' }}>
                            {enviando ? 'A enviar…' : <>{liquid ? <ValidityIcon name="send" fallback="✉" /> : '✉'} Enviar agora</>}
                        </button>
                        <button onClick={() => setMostrarLembrete(v => !v)} style={{ ...btnSec, fontSize: '0.78rem', padding: '5px 12px' }}>
                            {mostrarLembrete ? 'Fechar' : 'Configurar'}
                        </button>
                    </div>
                </div>
                {mostrarLembrete && lembrete && (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid #c7d2fe' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#3730a3', cursor: 'pointer' }}>
                            <input type="checkbox" checked={lembrete.lembrete_validades_ativo === 'true'}
                                onChange={e => updLembrete({ lembrete_validades_ativo: e.target.checked ? 'true' : 'false' })} /> Ativos
                        </label>
                        <div>
                            <label style={lbl}>Dia da semana</label>
                            <select value={lembrete.lembrete_validades_diasemana} onChange={e => updLembrete({ lembrete_validades_diasemana: e.target.value })} style={inp}>
                                {DIAS_SEMANA.map((d, i) => <option key={i} value={i}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={lbl}>Horizonte (dias)</label>
                            <input type="number" min="1" max="365" value={lembrete.lembrete_validades_dias}
                                onChange={e => updLembrete({ lembrete_validades_dias: e.target.value })} style={{ ...inp, width: 80 }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 180 }}>
                            <label style={lbl}>Email (vazio = email de alertas)</label>
                            <input value={lembrete.lembrete_validades_email} onChange={e => updLembrete({ lembrete_validades_email: e.target.value })}
                                placeholder="email de alertas da empresa" style={{ ...inp, width: '100%' }} />
                        </div>
                        <button onClick={guardarLembrete} style={{ ...btnPri, padding: '7px 14px' }}>Guardar</button>
                    </div>
                )}
                {msgLembrete && <div style={{ marginTop: 8, fontSize: '0.82rem', color: msgLembrete.startsWith('✓') ? '#166534' : '#991b1b' }}>{msgLembrete}</div>}
            </div>

            {/* Cartões-resumo */}
            <div className="validades-summary" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ flex: '1 1 140px', background: nExpirados ? '#fee2e2' : '#f0fdf4', border: `1px solid ${nExpirados ? '#fecaca' : '#bbf7d0'}`, borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: nExpirados ? '#991b1b' : '#166534' }}>{nExpirados}</div>
                    <div style={{ fontSize: '0.82rem', color: nExpirados ? '#991b1b' : '#166534' }}>Expirados</div>
                </div>
                <div style={{ flex: '1 1 140px', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#854d0e' }}>{n30}</div>
                    <div style={{ fontSize: '0.82rem', color: '#854d0e' }}>A expirar em ≤ 30 dias</div>
                </div>
                <div style={{ flex: '1 1 140px', background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#075985' }}>{itens.length}</div>
                    <div style={{ fontSize: '0.82rem', color: '#075985' }}>Total no horizonte de {horizonte} dias</div>
                </div>
            </div>

            {/* Controlo */}
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
                <div>
                    <label style={lbl}>Horizonte</label>
                    <select value={horizonte} onChange={e => setHorizonte(parseInt(e.target.value, 10))} style={inp}>
                        {[30, 60, 90, 180, 365].map(h => <option key={h} value={h}>{h} dias</option>)}
                    </select>
                </div>
                <div style={{ flex: '1 1 220px' }}>
                    <label style={lbl}>Pesquisar</label>
                    <input style={{ ...inp, width: '100%', boxSizing: 'border-box' }} placeholder="Nome, tipo, nº série…"
                        value={pesquisa} onChange={e => setPesquisa(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {categorias.map(c => (
                        <button key={c} aria-pressed={catFiltro === c} onClick={() => setCatFiltro(c)}
                            style={{
                                padding: '7px 12px', borderRadius: 999, fontSize: '0.82rem', cursor: 'pointer',
                                border: `1px solid ${catFiltro === c ? '#4338ca' : '#cbd5e1'}`,
                                background: catFiltro === c ? '#eef2ff' : '#fff',
                                color: catFiltro === c ? '#4338ca' : '#475569', fontWeight: catFiltro === c ? 700 : 500,
                            }}>
                            {c === 'Todos' ? 'Todos' : <>{liquid ? <ValidityIcon name={{Trabalhadores:'users',Equipamentos:'equipment',Instalações:'building',Veículos:'vehicle',Empresa:'building',RMM:'settings'}[c]} fallback={CAT_ICON[c] || ''} /> : CAT_ICON[c]} {c}</>}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tabela */}
            {loading ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>A carregar validades…</div>
            ) : visiveis.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
                    ✅ Sem validades expiradas ou a expirar {catFiltro !== 'Todos' ? `em ${catFiltro}` : ''} neste horizonte.
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="standard-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0', color: '#475569' }}>
                                <th style={{ padding: '8px 10px' }}>Estado</th>
                                <th style={{ padding: '8px 10px' }}>Validade</th>
                                <th style={{ padding: '8px 10px' }}>Categoria</th>
                                <th style={{ padding: '8px 10px' }}>Tipo</th>
                                <th style={{ padding: '8px 10px' }}>Entidade</th>
                                <th style={{ padding: '8px 10px' }}>Detalhe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visiveis.map((i, idx) => {
                                const e = estadoInfo(i.dias);
                                return (
                                    <tr key={`${i.categoria}-${i.tipo}-${i.ref_id}-${idx}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '8px 10px' }}>
                                            <span style={{ background: e.bg, color: e.fg, padding: '3px 10px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{e.label}</span>
                                        </td>
                                        <td style={{ padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtData(i.validade)}</td>
                                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{liquid ? <ValidityIcon name={{Trabalhadores:'users',Equipamentos:'equipment',Instalações:'building',Veículos:'vehicle',Empresa:'building',RMM:'settings'}[i.categoria]} fallback={CAT_ICON[i.categoria] || ''} /> : CAT_ICON[i.categoria] || ''} {i.categoria}</td>
                                        <td style={{ padding: '8px 10px' }}>{i.tipo}</td>
                                        <td style={{ padding: '8px 10px', fontWeight: 600 }}>{i.entidade}</td>
                                        <td style={{ padding: '8px 10px', color: '#64748b' }}>{i.detalhe || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PainelValidades;
